import { createSelector } from "@reduxjs/toolkit";

import { selectActiveBreakoutRoomForUserId } from "./breakout";
import { selectStage } from "./stage";

import {
  MinervaResourceSubtypeType,
  ResourceType,
  ResourceTypeType,
  RootState,
  VideoStateType,
} from "shared/types";

export const selectPreclassGroups = () => (state: RootState) =>
  state.classroom.client.preclass_groups;

export const selectPreclassResourceId = () => (state: RootState) =>
  state.classroom.client.preclass_resource_id;

export const selectResourceById = (id: string) => (state: RootState) =>
  state.classroom.client.resources[id];

const _selectResourcesByType = (type: ResourceTypeType) => (state: RootState) =>
  Object.values(state.classroom.client.resources).filter((resource) => resource.type === type);

export const selectResources = () => (state: RootState) => state.classroom.client.resources;

export const selectResourcesByIds = (ids: string[]) =>
  createSelector([selectResources(), () => ids], (resources, _ids) =>
    Object.values(resources).filter((resource) => _ids.includes(resource.id)),
  );

export const selectLiveCaptionResource = () => (state: RootState) => {
  const resources = state.classroom.client.resources;
  return Object.values(resources).find(
    (resource) =>
      resource.type === ResourceTypeType.ForumDoc &&
      resource.subtype === MinervaResourceSubtypeType.CaptionsDoc,
  );
};

export const _selectPlayingVideoResources = () =>
  createSelector(
    [_selectResourcesByType(ResourceTypeType.Video), _selectResourcesByType(ResourceTypeType.File)],
    (videoResources, fileResources) => {
      const playingVideoResources = videoResources.filter(
        (resource) => resource.video_state === VideoStateType.Playing,
      );
      const playingFileResources = fileResources.filter(
        (resource) =>
          resource.url.toLowerCase().endsWith(".mp4") &&
          resource.video_state === VideoStateType.Playing,
      );
      return playingVideoResources.concat(playingFileResources);
    },
  );

export const selectIsVideoResourcePlayingForUser = (userId: string) =>
  createSelector(
    [selectActiveBreakoutRoomForUserId(userId), selectStage(), _selectPlayingVideoResources()],
    (activeBreakoutRoom, stage, videoResources) => {
      if (!videoResources) {
        return false;
      }
      if (activeBreakoutRoom) {
        return activeBreakoutRoom.panes.some((pane) =>
          videoResources.some((resource) => resource.id === pane.content_id),
        );
      }
      return stage.panes.some((pane) =>
        videoResources.some((resource) => resource.id === pane.content_id),
      );
    },
  );
